<div class="rule-builder">

  <!-- ── Help section ────────────────────────────────────────────────── -->
  <div class="help-section">
    <button type="button" class="help-toggle no-btn" (click)="toggleHelp()">
      <span class="help-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      </span>
      <span class="help-title">How to create rule logic</span>
      <span class="help-chevron" [class.expanded]="isHelpExpanded">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    </button>

    @if (isHelpExpanded) {
      <div class="help-body">
        <ul class="help-list">
          <li><strong>Groups:</strong> Create up to 2 groups and use the group-level AND/OR operator to combine them.</li>
          <li><strong>Conditions:</strong> Add up to 2 conditions per group. Each condition can have an AND/OR operator.</li>
          <li><strong>Property: </strong> Fixed to <code>segments</code>. Use the dropdown to pick segments.</li>
          <li><strong>Segments:</strong> Select segments using ALL (match all selected) or ANY (match at least one) operators.</li>
          <li><strong>Logic:</strong> The rule evaluates from top to bottom, combining groups and conditions based on their operators.</li>
          @if (enableRuleLogicJson) {
            <li><strong>JSON Panel:</strong> The <em>Rule Logic JSON</em> panel at the bottom updates live as you build.</li>
          }
        </ul>
      </div>
    }
  </div>

  <!-- ── Groups loop ──────────────────────────────────────────────────── -->
  @for (group of groups; track $index; let gIdx = $index) {

    <!-- Connector between groups -->
    @if (gIdx > 0) {
      <div class="group-connector">
        <button type="button"
          class="connector-pill"
          [class.is-or]="groupOperator === 'OR'"
          [disabled]="isViewOnly"
          (click)="setGroupOperator(groupOperator === 'AND' ? 'OR' : 'AND')">
          {{ groupOperator }}
        </button>
      </div>
    }

    <div class="group-card" [class.group-card--error]="submitted && isGroupAllEmpty(gIdx)">

      <!-- Group header: condition-operator pill + title + optional delete -->
      <div class="group-header">
        <button type="button"
          class="group-op-badge"
          [class.is-or]="group.conditionOperator === 'OR'"
          [disabled]="isViewOnly || group.conditions.length < 2"
          (click)="setConditionOperator(gIdx, group.conditionOperator === 'AND' ? 'OR' : 'AND')"
          [title]="group.conditions.length < 2 ? 'Add a second condition to use this operator' : 'Toggle condition operator'">
          {{ group.conditionOperator }}
        </button>
        <span class="group-title">Condition Group {{ gIdx + 1 }}</span>
        @if (!isViewOnly && groups.length > 1) {
          <button type="button" class="delete-btn no-btn" (click)="removeGroup(gIdx)" title="Remove group">
            <i class="material-icons">delete_outline</i>
          </button>
        }
      </div>

      <div class="group-divider"></div>

      <!-- Conditions loop -->
      @for (condition of group.conditions; track $index; let cIdx = $index) {

        <!-- Separator between conditions -->
        @if (cIdx > 0) {
          <div class="condition-sep">
            <span class="condition-sep-label">{{ group.conditionOperator }}</span>
          </div>
        }

        <div class="condition-block" [class.condition-block--error]="submitted && isConditionEmpty(gIdx, cIdx)">

          <!-- Delete condition (only when more than 1) -->
          @if (!isViewOnly && group.conditions.length > 1) {
            <div class="condition-delete-row">
              <button type="button" class="delete-btn-sm no-btn" (click)="removeCondition(gIdx, cIdx)" title="Remove condition">
                <i class="material-icons">delete_outline</i>
              </button>
            </div>
          }

          <!-- Property / Operator table -->
          <div class="prop-op-table">
            <div class="prop-op-headers">
              <span class="col-header">PROPERTY</span>
              <span class="col-header">OPERATOR</span>
            </div>
            <div class="prop-op-values">
              <span class="prop-value-text">segments</span>
              <button type="button"
                class="op-value-btn"
                [class.is-any]="condition.segmentOperator === 'ANY'"
                [disabled]="isViewOnly || condition.segments.length <= 1"
                [title]="condition.segments.length <= 1 ? 'Select more than one segment to toggle operator' : 'Toggle operator'"
                (click)="setSegmentOperator(gIdx, cIdx, condition.segmentOperator === 'ALL' ? 'ANY' : 'ALL')">
                {{ condition.segmentOperator }}
              </button>
            </div>
          </div>

          <!-- Segment dropdown -->
          <div class="segment-field">
            <div class="rb-dropdown-wrapper"
                 appClickedOutside
                 (clickOutside)="closeDropdown(gIdx, cIdx)">

              <!-- Trigger -->
              <div class="rb-dropdown-trigger"
                   [class.open]="condition.dropdownOpen"
                   [class.is-invalid]="submitted && isConditionEmpty(gIdx, cIdx)"
                   (click)="!isViewOnly && toggleDropdown(gIdx, cIdx, $event)">

                <span class="rb-dropdown-display">
                  @if (condition.segments.length > 0) {
                    <div class="chips">
                      @for (seg of condition.segments | slice:0:3; track $index; let sIdx = $index) {
                        <span>
                          {{ getSegmentName(seg) }}
                          @if (!isViewOnly) {
                            <i (click)="removeSegment(gIdx, cIdx, sIdx, $event)">×</i>
                          }
                        </span>
                      }
                      @if (condition.segments.length > 3) {
                        <span class="more">+{{ condition.segments.length - 3 }} more</span>
                      }
                    </div>
                  } @else {
                    <span class="rb-placeholder">Select segment list</span>
                  }
                </span>
              </div>

              <!-- Dropdown content -->
              @if (condition.dropdownOpen) {
                <div class="rb-dropdown-content" (click)="$event.stopPropagation()">

                  <!-- Search -->
                  <div class="rb-search">
                    <input type="text"
                           class="rb-search-input"
                           placeholder="Search By Select segment list"
                           [ngModel]="condition.searchTerm"
                           (ngModelChange)="updateSearch(gIdx, cIdx, $event)" />
                    <div class="rb-search-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                    </div>
                  </div>

                  <!-- Select All / Clear All -->
                  <div class="rb-select-all">
                    <label class="rb-checkbox-label">
                      <input type="checkbox"
                             [checked]="isAllSelected(gIdx, cIdx)"
                             (change)="toggleSelectAll(gIdx, cIdx, $event)" />
                      <span class="rb-checkbox-custom"></span>
                      <span class="rb-option-text">Select All</span>
                    </label>
                    <button type="button" class="rb-clear-all no-btn" (click)="clearAllSegments(gIdx, cIdx, $event)">Clear All</button>
                  </div>

                  <!-- Options -->
                  <div class="rb-dropdown-options">
                    @for (seg of getFilteredSegments(gIdx, cIdx); track seg.value) {
                      <div class="rb-option-item">
                        <label class="rb-checkbox-label">
                          <input type="checkbox"
                                 [checked]="isSegmentSelected(gIdx, cIdx, seg.value)"
                                 (change)="toggleSegment(gIdx, cIdx, seg.value, $event)" />
                          <span class="rb-checkbox-custom"></span>
                          <span class="rb-option-text">{{ seg.name }}</span>
                        </label>
                      </div>
                    }
                    @if (getFilteredSegments(gIdx, cIdx).length === 0) {
                      <div class="rb-no-options">No segments found</div>
                    }
                  </div>
                </div>
              }

            </div>

            @if (submitted && isConditionEmpty(gIdx, cIdx)) {
              <div class="invalid-feedback">Please select at least one segment</div>
            }
          </div>

        </div>
      }

      <!-- Add condition -->
      @if (!isViewOnly && group.conditions.length < MAX_CONDITIONS) {
        <div class="add-condition-row">
          <button type="button" class="add-condition-btn no-btn" (click)="addCondition(gIdx)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            + Add Condition
          </button>
        </div>
      }

    </div>
  }

  <!-- ── Add group button ──────────────────────────────────────────────── -->
  @if (!isViewOnly && groups.length < MAX_GROUPS) {
    <button type="button" class="add-group-btn" (click)="addGroup()">
      + Add Group
    </button>
  }

  @if (enableRuleLogicJson) {
    <div class="json-section">
      <button type="button" class="json-toggle no-btn" (click)="toggleJson()">
        <span class="json-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="16 18 22 12 16 6"></polyline>
            <polyline points="8 6 2 12 8 18"></polyline>
          </svg>
        </span>
        <span class="json-title">Rule Logic JSON</span>
        <span class="json-chevron" [class.expanded]="isJsonExpanded">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </span>
      </button>

      @if (isJsonExpanded) {
        <div class="json-body">
          <pre class="json-code">{{ getDisplayJson() }}</pre>
        </div>
      }
    </div>
  }

</div>
